/**
 * /ai-coding 獲得ファネルの共通スキーマ。
 *
 * Search → Decision/Interactive → Learn/Challenge → Share の遷移を
 * PV ではなくイベントで測る。イベント名と共通プロパティの型はこの
 * ファイルで閉じる (job-type-diagnosis-analytics.ts と同じ方針)。
 * イベントや値を足すときは
 * docs/implementation/acquisition-funnel-events-2026-09-17.md も更新する。
 */
import {
  currentLearningDeviceType,
  type LearningDeviceType,
  type LearningUserStatus,
} from "./learning-events";

/**
 * ファネルが扱う AI コーディングツール/クライアント。
 * entity ハブのイベント名 `${entity}_hub_view` の語にもなるので、
 * 増やすときは PostHog 側の insight と slug を揃える。
 */
export type AcquisitionEntity =
  | "codex" // OpenAI Codex (CLI / IDE 拡張)
  | "claude_code" // Anthropic Claude Code
  | "cursor"
  | "copilot" // GitHub Copilot
  | "windsurf"
  | "gemini_cli" // Google Gemini CLI
  | "vscode"; // VS Code 本体 (Copilot 等のホストとしての選択肢)

/**
 * 面の系統。どのイベントも必ずどれかの面に属する。
 * entity ハブは当面 /learn/tracks/claude-code など既存頁が兼ねることがある。
 */
export type AcquisitionPageType =
  | "hub" // /ai-coding トップハブ
  | "entity_hub" // ツール別ハブ
  | "compare" // ツール比較
  | "benchmark" // ベンチマーク/実測
  | "pricing" // 料金 (ツール別料金とスクール料金の両方)
  | "security" // セキュリティ/企業導入の検証
  | "setup" // 導入・設定手順 (クライアント別タブ/MCP 連携を含む)
  | "troubleshoot" // 症状からの逆引き
  | "diagnosis"; // ツール選定診断

/**
 * 訪問者がその面に持ち込む検索意図。where-to-invest.md の
 * やり方/意味/道具 の分類を、このファネルの遷移判定に使える粒度に寄せたもの。
 */
export type AcquisitionIntent =
  | "choose" // どれを選ぶか (ハブ/比較/ベンチマーク/診断)
  | "setup" // 導入・設定したい (セットアップ手順/MCP/CLI)
  | "fix" // 症状・エラーを直したい (トラブルシュート)
  | "learn" // 使い方・概念を学びたい (entity ハブの学習節)
  | "buy"; // 料金・導入を検討したい (pricing/security)

/**
 * 発火場所。ページ内のどの面がイベントを出したか。
 * "page" は view 系イベントがページ本体から出るときの値。
 */
export type AcquisitionSource =
  | "page"
  | "tool_grid" // ハブ等のツール選択グリッド
  | "nav"
  | "compare_table"
  | "cta_block"
  | "client_tabs" // クライアント切替タブ
  | "setup_step"
  | "symptom_list" // 症状一覧
  | "command_block" // コマンド/コードブロック
  | "diagnosis_result"
  | "share_block"
  | "related_links";

/**
 * 遷移/クリックの行き先の系統。view 系・その場で完結するイベントでは省略。
 */
export type AcquisitionTargetType =
  | "hub"
  | "entity_hub"
  | "compare"
  | "benchmark"
  | "pricing"
  | "security"
  | "setup"
  | "troubleshoot"
  | "mcp" // MCP 連携手順
  | "diagnosis"
  | "learn" // /learn/* (コース/レッスン/トラック)
  | "challenge" // /challenges/*
  | "tool" // /tools/*
  | "external"; // 公式ドキュメント等サイト外

/**
 * テスト/本番の切り分け。PostHog はプロジェクト 1 本で、ローカルの
 * `bun run dev` も wrangler vars の本番キーを使うので、イベントを
 * 分ける仕組みが無いと dev の試行が本番の数に混ざる。
 * `$host` でも弾けるが、面の規約として property で持たせる。
 */
export type AcquisitionEnvironment = "production" | "development";

const PRODUCTION_HOSTNAMES: ReadonlySet<string> = new Set([
  "yumesaku.tech",
  "www.yumesaku.tech",
]);

export function acquisitionEnvironment(
  hostname?: string,
): AcquisitionEnvironment {
  const host =
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");
  return PRODUCTION_HOSTNAMES.has(host) ? "production" : "development";
}

/** entity ハブの view イベント名。`claude_code` → `claude_code_hub_view` */
export type AcquisitionEntityHubViewEvent = `${AcquisitionEntity}_hub_view`;

export function entityHubViewEvent(
  entity: AcquisitionEntity,
): AcquisitionEntityHubViewEvent {
  return `${entity}_hub_view`;
}

/**
 * /ai-coding ファネルの計測名。増やすときは PostHog の insight 側も揃える。
 *
 * `diagnosis_start` / `diagnosis_complete` は職種診断
 * (lib/job-type-diagnosis-analytics.ts) が同名を使っている。
 * 区別は `page_type` で行う — 職種診断のイベントには page_type が無いので、
 * ファネルのクエリは `page_type is set` / `page_type = "diagnosis"` で絞る。
 */
export type AcquisitionEventName =
  | "ai_coding_hub_view"
  | "ai_coding_tool_select"
  | AcquisitionEntityHubViewEvent
  | "pricing_view"
  | "security_view"
  | "compare_view"
  | "benchmark_view"
  | "troubleshoot_symptom_select"
  | "codex_cli_command_copy"
  | "compare_cta_click"
  | "mcp_integration_click"
  | "client_tab_change"
  | "setup_step_complete"
  | "connection_test_click"
  | "diagnosis_start"
  | "diagnosis_complete"
  | "result_share"
  | "recommended_challenge_start"
  /*
   * 診断結果の learn/tool 系 CTA。challenge 行きは名前のある
   * recommended_challenge_start を使い、それ以外の推薦導線はこちら
   */
  | "recommended_cta_click";

export interface AcquisitionEventProperties {
  /** 面の系統。全イベント必須 */
  page_type: AcquisitionPageType;
  /** その面が受ける検索意図。全イベント必須 */
  intent: AcquisitionIntent;
  /** 発火した場所。全イベント必須 (view 系は "page") */
  source: AcquisitionSource;
  /** 対象ツール/クライアント。単一ツールに紐づくイベントでは必須 */
  entity?: AcquisitionEntity;
  /** 遷移先の系統。クリック/遷移系イベントでは必須 */
  target_type?: AcquisitionTargetType;
  /** 遷移先の具体的な値 (slug / path / "x" 等のシェア先) */
  target?: string;
  /** セットアップ手順の id/番号、比較表のセル名など */
  step?: string;
  /** トラブルシュートで選んだ症状の id */
  symptom?: string;
  /** コピーしたコマンド名などの短い識別子 */
  command?: string;
  device_type?: LearningDeviceType;
  environment?: AcquisitionEnvironment;
  user_status?: LearningUserStatus;
  /** 面固有の値 (診断の所要時間・比較の軸名など) をそのまま乗せる */
  [property: string]: string | number | boolean | undefined;
}

export type AcquisitionTrackEvent = (
  event: string,
  properties?: Record<string, unknown>,
) => void;

/**
 * entity ハブとして機能している既存ページの対応表。
 * /ai-coding/<entity> ができたらそちらが正規のハブになる。
 * 対応を足すときはここに1行。
 */
const TRACK_SLUG_TO_ENTITY: Record<string, AcquisitionEntity> = {
  "claude-code": "claude_code",
};

export function acquisitionEntityForTrack(
  trackSlug: string,
): AcquisitionEntity | undefined {
  return TRACK_SLUG_TO_ENTITY[trackSlug];
}

/**
 * Analytics is observational. A capture failure must never block rendering or
 * the navigation that the visitor explicitly requested.
 */
export function captureAcquisitionEvent(
  trackEvent: AcquisitionTrackEvent,
  event: AcquisitionEventName,
  properties: AcquisitionEventProperties,
): void {
  try {
    trackEvent(
      event,
      Object.fromEntries(
        Object.entries({
          device_type: currentLearningDeviceType(),
          environment: acquisitionEnvironment(),
          ...properties,
        }).filter((entry) => entry[1] !== undefined),
      ),
    );
  } catch {
    // Fail open: the funnel page matters more than telemetry.
  }
}
